import classnames from 'classnames';
import { generateCompStylers } from '../../../common/utils/styles';
import { useCallback, useRef, useEffect } from 'react';
import { getBoundingTopSafari, useWindowDimensionsHeight } from '../../../common/utils/windowDimensions';
import { checkBrowser } from '../../../common/utils/utils';

const getCustomStyles = (name, index, gridStyles) => gridStyles?.[name] || gridStyles?.[index] || {};

export const getTdStyles = (css, rowData, cellData, cellIndex, gridStyles, thead, widthArr, sticky) => {
    return {
        style: {
            whiteSpace: 'initial',
            ...(widthArr[cellIndex] || {}),
            ...(gridStyles?.td?.style || {}),
            ...(getCustomStyles(cellData, cellIndex, gridStyles).td?.style || {}),
            ...(rowData?.name?.alias && gridStyles?.cellStyles
                ? {
                      ...gridStyles?.cellStyles[cellData]?.td?.style,
                      ...gridStyles?.cellStyles[`${cellData}_${rowData?.name?.alias}`]?.td?.style
                  }
                : {})
        },
        className: classnames(
            !!widthArr[cellIndex] ? '' : css.widthFitContent,
            !sticky?.leftStickyColumnCount && css.td,
            thead.length === cellIndex + 1 ? css.tdLast : {},
            gridStyles?.td?.className,
            getCustomStyles(cellData, cellIndex, gridStyles).td?.className,
            rowData?.name?.alias && gridStyles?.cellStyles
                ? [
                      gridStyles?.cellStyles[cellData]?.td?.className,
                      gridStyles?.cellStyles[`${cellData}_${rowData?.name?.alias}`]?.td?.className
                  ]
                : {}
        )
    };
};

export const getTdActiveRowStyles = (css, rowData, cellData, cellIndex, gridStyles, thead) => {
    const { stylers, classes } = generateCompStylers(gridStyles.activeRow);
    return {
        style: {
            ...(gridStyles?.activeRow?.style || {}),
            ...stylers[rowData.activeRow],
            ...(getCustomStyles(cellData, cellIndex, gridStyles).activeRow?.style || {}),
            ...(rowData.addedComponent ? { ...(stylers.parentDiv || {}) } : {}),
            ...(rowData?.name?.alias && gridStyles?.cellStyles
                ? {
                      ...gridStyles?.cellStyles[cellData]?.activeRow?.style,
                      ...gridStyles?.cellStyles[`${cellData}_${rowData?.name?.alias}`]?.activeRow?.style
                  }
                : {})
        },
        className: classnames(
            css.activeRow,
            classes[rowData.activeRow],
            cellIndex === 0 && css.activeRowFirst,
            thead.length === cellIndex + 1 && css.activeRowLast,
            gridStyles?.activeRow?.className,
            getCustomStyles(cellData, cellIndex, gridStyles).activeRow?.className,
            rowData.addedComponent && classes.parentDiv,
            rowData?.name?.alias && gridStyles?.cellStyles
                ? [
                      gridStyles?.cellStyles[cellData]?.activeRow?.className,
                      gridStyles?.cellStyles[`${cellData}_${rowData?.name?.alias}`]?.activeRow?.className
                  ]
                : {}
        )
    };
};
export const getTdFixedRowStyles = (css, cellIndex, gridStyles, thead, type) => {
    const { stylers, classes } = generateCompStylers(gridStyles);

    return {
        style: stylers?.fixedColumn || {},
        className: classnames(
            css.activeRow || {},
            css[type] || {},
            cellIndex === 0 ? css.activeRowFirst : {},
            thead.length === cellIndex + 1 ? css.activeRowLast : {},
            classes?.fixedColumn
        )
    };
};

export const getTdDivStyles = (css, rowData, cellData, cellIndex, gridStyles, thead, tbody, margin, openSwitcher) => {
    const { stylers, classes } = generateCompStylers(gridStyles?.td);
    const { stylersCus, classesCus } = generateCompStylers(getCustomStyles(cellData, cellIndex, gridStyles).td, 'Cus');
    const { stylersCell, classesCell } = generateCompStylers(
        rowData?.name?.alias && gridStyles?.cellStyles && gridStyles?.cellStyles[cellData]?.td,
        'Cell'
    );
    const { stylersCell2, classesCell2 } = generateCompStylers(
        rowData?.name?.alias && gridStyles?.cellStyles && gridStyles?.cellStyles[`${cellData}_${rowData?.name?.alias}`]?.td,
        'Cell2'
    );

    return {
        style: {
            ...(cellIndex === 0 ? margin(Math.max(rowData.depth - tbody[0].depth, 0)) : {}),
            ...stylers?.div,
            ...(rowData.addedComponent ? { ...(stylers.parentDiv || {}) } : {}),
            ...(stylersCus.div || {}),
            ...(rowData.addedComponent ? { ...stylersCus.parentDiv } : {}),
            ...stylersCell?.div,
            ...stylersCell2?.div,
            ...(thead[cellIndex]?.nowrap?.td ? { whiteSpace: 'nowrap' } : {}),
            ...(thead[cellIndex]?.nowrap?.td === false ? { whiteSpace: 'normal' } : {})
        },
        className: classnames(
            openSwitcher ? css.tdFixedHeight : {},
            (cellIndex === 0 && !['center', 'right'].includes(thead[cellIndex]?.align)) || thead[cellIndex]?.align === 'left'
                ? css.tdAlignLeft
                : thead[cellIndex]?.align === 'right'
                ? css.tdAlignRight
                : css.tdAlignCenter,
            classes.div || {},
            rowData.addedComponent ? classes.parentDiv : {},
            classesCus.div || {},
            rowData.addedComponent ? classesCus.parentDiv : {},
            classesCell.div,
            classesCell2.div
        )
    };
};

export const getTdSwitcherStyles = (css, rowData, cellData, cellIndex, gridStyles) => {
    const { stylers, classes } = generateCompStylers(gridStyles?.td);
    const { stylersCus, classesCus } = generateCompStylers(getCustomStyles(cellData, cellIndex, gridStyles).td, 'Cus');
    const { stylersCell, classesCell } = generateCompStylers(
        rowData?.name?.alias && gridStyles?.cellStyles && gridStyles?.cellStyles[cellData]?.td,
        'Cell'
    );
    const { stylersCell2, classesCell2 } = generateCompStylers(
        rowData?.name?.alias && gridStyles?.cellStyles && gridStyles?.cellStyles[`${cellData}_${rowData?.name?.alias}`]?.td,
        'Cell2'
    );

    return {
        style: {
            ...stylers.switcher,
            ...(stylersCus.switcher || {}),
            ...stylersCell.switcher,
            ...stylersCell2.switcher
        },
        className: classnames(
            css.switcherWrap,
            classes.switcher,
            classesCus.switcher,
            classesCell.switcher,
            classesCell2.switcher
        )
    };
};

export const getThStyles = (css, gridStyles, widthArr, idx, data, thead, sticky, fixedLastColumn) => {
    return {
        style: {
            ...(fixedLastColumn ? { paddingRight: '10px' } : {}),
            ...(widthArr[idx] || {}),
            ...(gridStyles.th?.style || {}),
            ...(getCustomStyles(data.name, idx, gridStyles).th?.style || {})
        },
        className: classnames(
            sticky.leftStickyColumnCount ? css.thSticky : css.th,
            gridStyles.th?.className || {},
            thead.length === idx + 1 ? css.tdLast : {},
            getCustomStyles(data.name, idx, gridStyles).th?.className || {}
        )
    };
};

export const getThDivStyles = (css, data, idx, gridStyles) => {
    const { stylers, classes } = generateCompStylers(gridStyles?.th);
    const { stylersCus, classesCus } = generateCompStylers(getCustomStyles(data.name, idx, gridStyles).th, 'Cus');

    return {
        style: {
            ...stylers.div,
            ...stylersCus.div,
            ...(data.nowrap?.th ? { whiteSpace: 'nowrap' } : {}),
            ...(data.nowrap?.th === false ? { whiteSpace: 'wrap' } : {})
        },
        className: classnames(
            css.thContent,
            (idx === 0 && !['center', 'right'].includes(data.align)) || data.align === 'left'
                ? css.tdAlignLeft
                : data.align === 'right'
                ? css.tdAlignRight
                : css.tdAlignCenter,
            classes.div || {},
            classesCus.div || {}
        )
    };
};

export const generateGridStyles = (gridStyles, name) => {
    if (!gridStyles || !gridStyles[name]) {
        return {
            stylers: {},
            classes: {},
            existCl: () => false,
            existSt: () => false
        };
    }
    const { stylers, classes } = generateCompStylers(gridStyles[name]);
    const existCl = (key) => {
        if (classes[key]) return classes[key];
        if (stylers[key] && !classes[key]) return '';
        return false;
    };
    const existSt = (key) => {
        if (stylers[key]) return stylers[key];
        if (classes[key] && !stylers[key]) return {};
        return false;
    };
    return {
        stylers,
        classes,
        existCl,
        existSt
    };
};
export const useFixedHead = (tableRef, refTable, refTableChild, fullScreen, rowRef, sticky, gridStyles, maxGridHeight) => {
    const { stylers } = generateCompStylers(gridStyles?.tableWrapper);
    let isSafari =
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
        /constructor/i.test(window.HTMLElement) ||
        (function (p) {
            return p.toString() === '[object SafariRemoteNotification]';
        })(!window['safari'] || typeof safari !== 'undefined');
    const func = useCallback(() => {
        if (!!tableRef.current) {
            let scrollOffsetTop = getBoundingTopSafari(tableRef.current);
            const offset = fullScreen ? getBoundingTopSafari(refTableChild.current) || 0 : 0;
            scrollOffsetTop = scrollOffsetTop - offset;
            if (isSafari && rowRef.current.childNodes) {
                if (scrollOffsetTop < 0) {
                    [].slice
                        .call(rowRef.current.childNodes)
                        .forEach((el) => (el.style.transform = `translateY(${checkBrowser(scrollOffsetTop) * -1}px)`));
                } else {
                    [].slice.call(rowRef.current.childNodes).forEach((el) => (el.style.transform = 'translateY(0px)'));
                }
            } else {
                if (scrollOffsetTop < 0) {
                    rowRef.current.style.transform = `translateY(${checkBrowser(scrollOffsetTop) * -1}px)`;
                    rowRef.current.style.position = 'relative';
                    rowRef.current.style.zIndex = 11;
                } else {
                    rowRef.current.style.transform = 'translateY(0px)';
                    rowRef.current.style.position = 'relative';
                    rowRef.current.style.zIndex = 11;
                }
            }
        }
    }, [fullScreen, isSafari, rowRef, tableRef, refTableChild]);
    useEffect(() => {
        if (sticky && !sticky.stickyHeaderCount) {
            func();
            const tableElement = refTableChild?.current;
            if (fullScreen) {
                tableElement.addEventListener('scroll', func);
                window.removeEventListener('scroll', func);
            } else {
                tableElement.removeEventListener('scroll', func);
                window.addEventListener('scroll', func);
            }

            return () => {
                tableElement.removeEventListener('scroll', func);
                window.removeEventListener('scroll', func);
            };
        }
    }, [func, sticky, fullScreen, refTableChild]);
    const windowHeight = useWindowDimensionsHeight();
    useEffect(() => {
        if (sticky && !sticky.stickyHeaderCount) {
            const tableElement = refTableChild?.current;
            if (tableElement) {
                if (fullScreen) {
                    const tableHeight = tableElement?.getBoundingClientRect().height;
                    const tableParentHeight = refTable?.current?.parentElement?.getBoundingClientRect().height;
                    const tableTop = getBoundingTopSafari(tableElement);
                    const tableParentTop = getBoundingTopSafari(refTable?.current?.parentElement);
                    const bottomOffset = tableParentTop + tableParentHeight - (tableTop + tableHeight);
                    const maxHeight = windowHeight - tableTop - bottomOffset;
                    if (maxHeight > 100) {
                        if (maxGridHeight) {
                            tableElement.style.maxHeight = maxGridHeight + 'px';
                        } else {
                            tableElement.style.height = Math.floor(maxHeight) + 'px';
                        }
                    } else {
                        tableElement.style.height = '100%';
                    }
                } else {
                    if (stylers?.wrapper?.height) {
                        tableElement.style.height = stylers?.wrapper?.height;
                    } else {
                        tableElement.style.removeProperty('height');
                    }
                }
            }
        }
    }, [fullScreen, refTableChild, refTable, sticky, windowHeight, stylers, maxGridHeight]);
};
export const useFixedColumnHeight = (rightStickyColumnName, gridName, windowDimensions, viewData) => {
    const timeout = useRef();
    useEffect(() => {
        if (rightStickyColumnName) {
            clearTimeout(timeout.current);
            timeout.current = setTimeout(() => {
                const cells = [...document.querySelectorAll(`[data-copy=${gridName}]`)];
                const copy = [...document.querySelectorAll(`[data-copy-fixed=${gridName}-last]`)];
                const cellsThead = [...document.querySelectorAll(`[data-thead-copy=${gridName}]`)];
                const copyThead = [...document.querySelectorAll(`[data-thead-copy-fixed=${gridName}-last]`)];

                if (copyThead[0]) {
                    copyThead[0].style.height = checkBrowser(cellsThead[0]?.getBoundingClientRect()?.height) + 'px';
                }
                for (let index = 0; index < cells.length; index++) {
                    const element = cells[index];
                    const copyElement = copy[index];
                    copyElement.style.height = checkBrowser(element.getBoundingClientRect()?.height) + 'px';
                }
            }, 100);
        }
        return () => {
            clearTimeout(timeout.current);
        };
    }, [windowDimensions, rightStickyColumnName, gridName, viewData]);
};
export const useDraggableScroll = (srollTableRef, tableRef, isFixedColumn) => {
    const isDragging = useRef();
    const startX = useRef();
    const startScrollLeft = useRef();
    useEffect(() => {
        if (srollTableRef?.current && !isFixedColumn) {
            var scrollableElement = srollTableRef?.current;

            const mousedown = function (e) {
                if (tableRef?.current?.scrollWidth > tableRef?.current?.clientWidth) {
                    isDragging.current = true;
                    startX.current = e.clientX;
                    startScrollLeft.current = scrollableElement.scrollLeft;
                    scrollableElement.style.cursor = 'grabbing';
                }
            };
            const mousemove = function (e) {
                if (!isDragging.current) return;

                var deltaX = e.clientX - startX.current;
                scrollableElement.scrollLeft = startScrollLeft.current - deltaX;
            };
            const mouseup = function () {
                isDragging.current = false;
                scrollableElement.style.cursor = '';
            };
            scrollableElement.addEventListener('mousedown', mousedown);
            document.addEventListener('mousemove', mousemove);
            document.addEventListener('mouseup', mouseup);
            return () => {
                scrollableElement.removeEventListener('mousedown', mousedown);
                document.removeEventListener('mousemove', mousemove);
                document.removeEventListener('mouseup', mouseup);
            };
        }
    }, [srollTableRef, tableRef, isFixedColumn]);
};
